import React, { useCallback, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  Panel,
} from "reactflow";
import type {
  Node,
  Edge,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  ReactFlowInstance,
} from "reactflow";
import "reactflow/dist/style.css";
import "../../styles/WorkflowCanvas.css";

interface WorkflowCanvasProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  onNodeClick: (event: React.MouseEvent, node: Node) => void;
  onEdgeClick: (event: React.MouseEvent, edge: Edge) => void;
  onPaneClick: () => void;
  onDrop: (event: React.DragEvent) => void;
  onDragOver: (event: React.DragEvent) => void;
  onInit: (instance: ReactFlowInstance) => void;
  nodeTypes: any;
  edgeTypes: any;
}

const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onEdgeClick,
  onPaneClick,
  onDrop,
  onDragOver,
  onInit,
  nodeTypes,
  edgeTypes,
}) => {
  const [showHelp, setShowHelp] = useState(false);

  const nodeColor = useCallback((node: Node) => {
    const category = node.data.category || "trigger";
    const colors = {
      trigger: "#2563eb",
      condition: "#f59e0b",
      action: "#10b981",
    };
    return colors[category as keyof typeof colors] || "#9ca3af";
  }, []);

  return (
    <div className="workflow-canvas">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={onInit}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        snapGrid={[10, 10]}
        snapToGrid
        selectionOnDrag
        multiSelectionKeyCode="Control"
        panOnDrag={false}
        fitView
        defaultEdgeOptions={{
          type: "custom",
          animated: true,
          style: { strokeWidth: 2 },
        }}
        proOptions={{ hideAttribution: true }}>
        <Background
          color="#d1d5db"
          gap={30}
          size={3}
          variant={BackgroundVariant.Cross}
        />
        <Controls showInteractive={false} position="bottom-left" />

        {/* Compact MiniMap */}
        <MiniMap
          nodeColor={nodeColor}
          nodeBorderRadius={8}
          nodeStrokeWidth={2}
          maskColor="rgba(0, 0, 0, 0.1)"
          position="bottom-right"
        />

        {/* Stats Panel with Help */}
        <Panel position="top-left" className="canvas-stats-panel">
          <div className="stats-item">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="9" y1="9" x2="15" y2="9" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
            <span>{nodes.length} Nodes</span>
          </div>

          <div className="stats-divider"></div>

          {/* Edges Count */}
          <div className="stats-item">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <polyline points="19 12 12 19 5 12" />
            </svg>
            <span>{edges.length} Edges</span>
          </div>

          <div className="stats-divider"></div>

          {/* Help Button */}
          <div
            className="stats-item help-trigger"
            onMouseEnter={() => setShowHelp(true)}
            onMouseLeave={() => setShowHelp(false)}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>Help</span>

            {/* Tooltip on Hover */}
            {showHelp && (
              <div className="help-tooltip">
                <div className="help-tooltip-arrow"></div>
                <div className="help-tooltip-content">
                  <strong>Quick Tips</strong>
                  <ul>
                    <li>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2">
                        <path d="M9 11l3 3L22 4" />
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                      </svg>
                      Drag nodes from sidebar
                    </li>
                    <li>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      Click edge to delete
                    </li>
                    <li>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2">
                        <rect
                          x="3"
                          y="11"
                          width="18"
                          height="11"
                          rx="2"
                          ry="2"
                        />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                      Del key removes selection
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
};

export default WorkflowCanvas;
